import { useCallback, useState } from "react"
import * as API from "services/api"
import { errorToast } from "components/shared/toast"

const LOAD_AUDITS_ERROR_MESSAGE = "Something went wrong. Unable to load the user's activation history."

const UNINITIALIZED = "uninitialized"
const INITIALIZING = "initializing"
const REFRESHING = "refreshing"
const LOADED = "loaded"

const useUserDiscardRelatedAudits = (userId) => {
  const [audits, setAudits] = useState([])
  const [status, setStatus] = useState(UNINITIALIZED)

  // A failed load returns the status it started from, so a first load can be attempted again and a
  // failed refresh leaves the audits already loaded in place.
  const fetchAudits = useCallback(async (previousStatus) => {
    setStatus(previousStatus === UNINITIALIZED ? INITIALIZING : REFRESHING)

    const response = await API.userDiscardRelatedAudits({ userId })

    if (response.ok) {
      setAudits(response.data)
      setStatus(LOADED)
      return
    }

    errorToast(LOAD_AUDITS_ERROR_MESSAGE)
    setStatus(previousStatus)
  }, [userId])

  // Called when the audits are first shown, so that they are not requested before then.
  const loadAudits = useCallback(() => {
    if (status !== UNINITIALIZED) return

    fetchAudits(status)
  }, [fetchAudits, status])

  // Before the audits have been shown there is nothing to refresh, and whenever they are shown they
  // are loaded fresh, so a change made first needs no reload of its own.
  const reloadAudits = useCallback(() => {
    if (status !== LOADED) return

    fetchAudits(status)
  }, [fetchAudits, status])

  return {
    audits,
    initializing: status === INITIALIZING,
    loadAudits,
    reloadAudits,
  }
}

export default useUserDiscardRelatedAudits
